import React from 'react';
import { Header, Segment } from 'semantic-ui-react';
import { ChartColumnIncreasing, ChartLine, Download } from 'lucide-react';
import { MultiselectField } from '../../../financialTables/configuration/fields';
import KeyValue from '../../../../structures/keyValePair';
import { Toggle } from '../../../toggleGroup';
import { AnnotationSize, AnnotationType, AnnotationTypeValue, ChartStyle } from '../../types';
import {
    useChartStoreChartActions,
    useChartStoreChartCfg,
    useChartStoreAvailablePeriods,
    useChartStoreSelectedPeriods,
    useEChartsRef,
    useChartData,
} from '../../context';

type ChartToolbarProps = {
    onDownloadClick?: () => void;
    hidePeriods?: boolean;
    extraControls?: React.ReactNode;
};

function ChartToolbar({ onDownloadClick, hidePeriods, extraControls }: ChartToolbarProps) {
    const chartCfg = useChartStoreChartCfg();
    const availablePeriods = useChartStoreAvailablePeriods();
    const selectedPeriods = useChartStoreSelectedPeriods();
    const { updateChartCfg, setSelectedPeriods, setChartStyle } = useChartStoreChartActions();
    const { chartReady } = useEChartsRef();
    const { hasChartData } = useChartData();

    return (
        <Segment size="mini" className="tableConfigSegment baseSettings flex flex-justify-space-between">
            {!hidePeriods && (
                <div className="configModule">
                    <Header as="h5">Periods</Header>
                    <MultiselectField
                        selected={selectedPeriods}
                        optionsKV={availablePeriods.map(KeyValue.createSame)}
                        onChange={setSelectedPeriods}
                    />
                </div>
            )}
            {extraControls}

            <div className="floatRight flex gap-20 flex-wrap">
                <div>
                    <Header as="h5">Baseline</Header>
                    <Toggle
                        activeValue={chartCfg.startFromZero}
                        onChange={(value: boolean) => updateChartCfg({ startFromZero: value })}
                        options={[new KeyValue(false, 'Auto'), new KeyValue(true, 'Zero')]}
                    />
                </div>

                <div className="configModule">
                    <Header as="h5">Chart Style</Header>
                    <Toggle
                        activeValue={chartCfg.style}
                        onChange={setChartStyle}
                        options={[
                            new KeyValue(ChartStyle.Line, <ChartLine size={20} strokeWidth={1} />),
                            new KeyValue(ChartStyle.Column, <ChartColumnIncreasing size={20} strokeWidth={1} />),
                        ]}
                    />
                </div>

                <div className="configModule">
                    <Header as="h5">Grid Lines</Header>
                    <Toggle
                        activeValue={chartCfg.showGridLines}
                        onChange={(value: boolean) => updateChartCfg({ showGridLines: value })}
                        options={[new KeyValue(true, 'Show'), new KeyValue(false, 'Hide')]}
                    />
                </div>

                <div className="configModule">
                    <Header as="h5">Annotation Type</Header>
                    <Toggle
                        activeValue={chartCfg.annotationType}
                        onChange={(value: AnnotationTypeValue) => updateChartCfg({ annotationType: value })}
                        options={Object.values(AnnotationType).map((v) => KeyValue.createSame(v))}
                    />
                </div>

                <div className="configModule">
                    <Header as="h5">Annotation Color</Header>
                    <Toggle
                        activeValue={chartCfg.matchSeriesColor}
                        onChange={(value: boolean) => updateChartCfg({ matchSeriesColor: value })}
                        options={[new KeyValue(true, 'Series'), new KeyValue(false, 'Black')]}
                    />
                </div>

                <div className="configModule">
                    <Header as="h5">Annotations</Header>
                    <Toggle
                        activeValue={chartCfg.showAnnotations}
                        onChange={(value: boolean) => updateChartCfg({ showAnnotations: value })}
                        options={[new KeyValue(true, 'Show'), new KeyValue(false, 'Hide')]}
                    />
                </div>

                <div className="configModule">
                    <Header as="h5">Annotations Size</Header>
                    <Toggle
                        activeValue={chartCfg.annotationSize}
                        onChange={(value: number) => updateChartCfg({ annotationSize: value })}
                        options={Object.entries(AnnotationSize).map(([k, v]) => new KeyValue(v, k))}
                    />
                </div>

                <div className="configModule">
                    <Header as="h5">Download</Header>
                    <div className="flex flex-align-center flex-justify-center">
                        <button
                            disabled={!chartReady || !hasChartData || !onDownloadClick}
                            onClick={onDownloadClick}
                            className={`download flex flex-align-center flex-justify-center checkboxBtn ${
                                chartReady && hasChartData ? 'checked' : ''
                            }`}
                        >
                            <Download size={20} strokeWidth={1} />
                        </button>
                    </div>
                </div>
            </div>
        </Segment>
    );
}

export default React.memo(ChartToolbar);
